import React, { useContext, useState } from 'react';
import { Star, Pencil, Trash2 } from 'lucide-react';
import sessionsService from '../../../services/sessionsServices';
import { TemporaryChatContext } from '../../../Context/temporaryChatContext';
import { useLocation, useNavigate } from 'react-router-dom';
import Loaders from '../../Loaders';
import RenameChatModal from '../RenameChatModal';
import DeleteChatModal from '../DeleteChatModal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from '../../Toast';
import { useSidebarSessions, sessionsInvalidateKey } from '../../../hooks';
import { CardMenu } from '../../ui';
import { cn } from '../../../lib/utils';
import { DEFAULT_PEPPER_ID } from '../../ChatSection/PolyChatHeader';

const Timeline = ['Today', 'Yesterday', 'Last 7 Days', 'Last 30 Days'];

const SessionsListSection = ({ selectedAgent }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { toggleRef } = useContext(TemporaryChatContext);

  const agentId = selectedAgent?.value || DEFAULT_PEPPER_ID;

  const payload = {
    platform_id: 'plateslabs',
    agent_id: selectedAgent?.value || '',
    project_id: 'test',
  };

  const { data, isPending } = useSidebarSessions(payload);
  const [deleteChat, setDeleteChat] = useState(null);
  const [renameChat, setRenameChat] = useState(null);

  const handleAddFavorite = useMutation({
    mutationFn: async id =>
      sessionsService?.updateSession({
        platform_id: 'plateslabs',
        session_id: id,
        agent_id: agentId,
        is_favorite: true,
      }),
    retry: 1,
    onSuccess: response => {
      Toast({ type: 'success', message: response.detail });
      queryClient.invalidateQueries({ queryKey: sessionsInvalidateKey });
    },
    onError: err => {
      Toast({ type: 'error', message: err.detail });
    },
  });

  const handleRemoveFavorite = useMutation({
    mutationFn: async id =>
      sessionsService?.updateSession({
        platform_id: 'plateslabs',
        session_id: id,
        agent_id: agentId,
        is_favorite: false,
      }),
    retry: 1,
    onSuccess: response => {
      Toast({ type: 'success', message: response.detail });
      queryClient.invalidateQueries({ queryKey: sessionsInvalidateKey });
    },
    onError: err => {
      Toast({ type: 'error', message: err.detail });
    },
  });

  const openChat = chat => {
    toggleRef(true);
    navigate(`/chat/${chat?.id}`);
  };

  const renderChatItem = (chat, { favorite } = {}) => {
    const active = location.pathname?.includes(chat?.id);
    return (
      <li key={chat.id}>
        <div
          className={cn(
            'group flex items-center gap-1 rounded-lg transition-colors',
            active ? 'bg-primary/10 text-primary' : 'text-muted hover:bg-ink/[0.03] hover:text-ink',
          )}>
          <button
            type="button"
            onClick={() => openChat(chat)}
            className="flex-1 min-w-0 text-left px-2.5 py-2 text-[13px] font-medium truncate"
            dangerouslySetInnerHTML={{ __html: chat?.title }}
          />
          <div
            className="pr-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={e => e.stopPropagation()}>
            <CardMenu
              triggerClassName="text-muted-dim hover:text-ink hover:bg-ink/5"
              items={[
                favorite
                  ? {
                      label: 'Remove from Favorites',
                      icon: Star,
                      onClick: () => handleRemoveFavorite.mutate(chat?.id),
                    }
                  : {
                      label: 'Add to Favorites',
                      icon: Star,
                      onClick: () => handleAddFavorite.mutate(chat?.id),
                    },
                {
                  label: 'Rename',
                  icon: Pencil,
                  onClick: () => setRenameChat(chat),
                },
                {
                  label: 'Delete',
                  icon: Trash2,
                  onClick: () => setDeleteChat(chat),
                  danger: true,
                },
              ]}
            />
          </div>
        </div>
      </li>
    );
  };

  const hasAnySessions =
    (data?.sessions?.Favorite?.length || 0) > 0 || Timeline.some(time => (data?.sessions?.[time]?.length || 0) > 0);

  return (
    <div className="pb-2">
      {isPending ? (
        <Loaders textLoader />
      ) : !hasAnySessions ? (
        <p className="px-2 py-3 text-xs text-muted-dim">No chats yet.</p>
      ) : (
        <>
          {data?.sessions?.Favorite?.length > 0 && (
            <div className="mb-4">
              <span className="block text-[11px] uppercase tracking-wide text-muted-dim font-medium mb-1.5 px-2">
                Favorites
              </span>
              <ul className="space-y-0.5">
                {data.sessions.Favorite.map(chat => renderChatItem(chat, { favorite: true }))}
              </ul>
            </div>
          )}

          {Timeline.map(time =>
            data?.sessions?.[time]?.length > 0 ? (
              <div key={time} className="mb-4">
                <span className="block text-[11px] uppercase tracking-wide text-muted-dim font-medium mb-1.5 px-2">
                  {time}
                </span>
                <ul className="space-y-0.5">{data.sessions[time].map(chat => renderChatItem(chat))}</ul>
              </div>
            ) : null,
          )}
        </>
      )}

      {renameChat && <RenameChatModal data={renameChat} onClose={() => setRenameChat(null)} />}

      {deleteChat && <DeleteChatModal onClose={() => setDeleteChat(null)} data={deleteChat} />}
    </div>
  );
};

export default SessionsListSection;
